import { useState } from "react";
import { MessageCircle, ArrowUpRight } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { CategorySheet } from "@/components/CategorySheet";
import { useLang } from "@/contexts/LangContext";
import { useSettings } from "@/contexts/SettingsContext";

export const ProductGrid = () => {
  const { t } = useLang();
  const { categories } = useSettings();
  const [openCat, setOpenCat] = useState(null);

  return (
    <section
      data-testid={HOME.menuSection}
      id="menu"
      className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="h-px w-8 bg-[#00ff41]" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#00ff41]">
              {t("menu_ribbon")}
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1]">
            {t("menu_title")}
          </h2>
          <p className="mt-3 max-w-xl text-neutral-400">
            {t("menu_sub_a")} {t("menu_sub_b")}
          </p>
        </div>
        <div className="rounded-2xl border border-[#00ff41]/30 bg-[#00ff41]/5 px-4 py-3 text-sm text-[#c8ffd6] max-w-sm">
          <p className="font-display font-bold text-[#00ff41]">{t("bottles_title")}</p>
          <p className="text-neutral-300 mt-1 leading-snug">{t("bottles_body")}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
        {categories.map((cat, i) => (
          <ProductCard key={cat.id} cat={cat} index={i} onOpen={() => setOpenCat(cat)} t={t} />
        ))}
      </div>

      <CategorySheet open={!!openCat} onClose={() => setOpenCat(null)} category={openCat} />
    </section>
  );
};

const STAGGER_STEP_MS = 40;
const MAX_STAGGER_DELAY_MS = 600;

const ProductCard = ({ cat, index, onOpen, t }) => (
  <button
    data-testid={HOME.productCard(cat.id)}
    onClick={onOpen}
    className="lift group relative block overflow-hidden rounded-3xl border border-white/10 bg-[#0a0a0a] aspect-[4/5] fade-up text-left w-full"
    style={{ animationDelay: `${Math.min(index * STAGGER_STEP_MS, MAX_STAGGER_DELAY_MS)}ms` }}
  >
    <img
      src={cat.image}
      alt={cat.name}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover opacity-80 group-hover:opacity-95 group-hover:scale-[1.04] transition-all duration-500"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/10" />

    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-[10px] font-mono tracking-widest text-[#00ff41] border border-[#00ff41]/30">
      {String(index + 1).padStart(2, "0")}
    </div>
    <div className="absolute top-3 right-3 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur border border-white/15 text-white group-hover:bg-[#00ff41] group-hover:text-black transition">
      <ArrowUpRight size={14} />
    </div>

    <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5">
      <h3 className="font-display text-lg sm:text-xl font-extrabold leading-tight tracking-tight">
        {cat.name}
      </h3>
      <p className="mt-1 text-xs sm:text-sm text-neutral-300/90 leading-snug line-clamp-2">
        {cat.blurb}
      </p>
      <div
        data-testid={HOME.productTextBtn(cat.id)}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#00ff41] px-3 py-1.5 text-xs font-display font-bold text-black group-hover:bg-white transition"
      >
        <MessageCircle size={12} />
        {t("view_items")}
      </div>
    </div>
  </button>
);

export default ProductGrid;
